import { useRecoilState } from "recoil"
import { CartAtom } from "../../atoms/accountAtom"
import styled from "styled-components"
import { Link, useNavigate } from "react-router-dom"
import { Minus, Plus, Trash2, ArrowLeft } from "lucide-react"

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
  gap: 1rem;
`

const BackButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s;

  &:hover {
    color: #0f172a;
  }
`

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #0f172a;
`

const CartContainer = styled.div`
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`

const CartTable = styled.div`
  width: 100%;
  border-collapse: collapse;
`

const CartHeader = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 1rem;
  padding: 1rem;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  font-weight: 500;
  color: #64748b;

  @media (max-width: 640px) {
    display: none;
  }
`

const CartItem = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr auto;
  gap: 1rem;
  padding: 1rem;
  align-items: center;
  border-bottom: 1px solid #e2e8f0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr auto;
    gap: 0.5rem;
  }
`

const ProductInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 640px) {
    grid-column: 1 / -1;
  }
`

const ProductImage = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 0.25rem;
  object-fit: cover;
`

const ProductDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`

const ProductTitle = styled.h3`
  font-weight: 500;
  color: #0f172a;
`

const ProductCategory = styled.span`
  font-size: 0.875rem;
  color: #64748b;
`

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 640px) {
    grid-column: 1;
    grid-row: 2;
  }
`

const QuantityButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 0.25rem;
  background: white;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #f1f5f9;
    color: #0f172a;
  }
`

const Quantity = styled.span`
  min-width: 1.5rem;
  text-align: center;
  font-weight: 500;
`

const Price = styled.div`
  font-weight: 500;
  color: #0f172a;

  @media (max-width: 640px) {
    grid-column: 1;
    grid-row: 3;
  }
`

const Total = styled.div`
  font-weight: 600;
  color: #0f172a;

  @media (max-width: 640px) {
    grid-column: 1;
    grid-row: 4;
  }
`

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 0.25rem;
  background: #fee2e2;
  color: #ef4444;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #fecaca;
  }

  @media (max-width: 640px) {
    grid-column: 2;
    grid-row: 1;
  }
`

const Summary = styled.div`
  padding: 1.5rem;
  border-top: 1px solid #e2e8f0;
  background: #f8fafc;
`

const SummaryContent = styled.div`
  max-width: 400px;
  margin-left: auto;
`

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  color: #64748b;
  font-size: 0.875rem;

  &:last-child {
    margin-top: 1rem;
    margin-bottom: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid #e2e8f0;
    font-size: 1.125rem;
    font-weight: 600;
    color: #0f172a;
  }
`

const CheckoutButton = styled.button`
  display: block;
  width: 100%;
  padding: 0.75rem;
  background: #0f172a;
  color: white;
  border: none;
  border-radius: 0.5rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #1e293b;
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`

const EmptyCart = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #64748b;
`

export default function Cart() {
  const navigate = useNavigate()
  const [cartItems, setCartItems] = useRecoilState(CartAtom)

  const updateQuantity = (id, delta) => {
    setCartItems((items) =>
      items.map((item) => {
        if (item.id === id) {
          const newQuantity = Math.min(10, Math.max(1, item.quantity + delta))
          return { ...item, quantity: newQuantity }
        }
        return item
      }),
    )
  }

  const removeItem = (id) => {
    setCartItems((items) => items.filter((item) => item.id !== id))
  }

  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const shipping = 0
  const total = subtotal + shipping

  if (cartItems.length === 0) {
    return (
      <Container>
        <Header>
          <BackButton to="/">
            <ArrowLeft size={20} />
            계속 쇼핑하기
          </BackButton>
          <Title>장바구니</Title>
        </Header>
        <CartContainer>
          <EmptyCart>
            <h2>장바구니가 비어있습니다</h2>
            <p>강좌를 둘러보고 장바구니에 담아보세요!</p>
          </EmptyCart>
        </CartContainer>
      </Container>
    )
  }

  return (
    <Container>
      <Header>
        <BackButton to="/">
          <ArrowLeft size={20} />
          계속 쇼핑하기
        </BackButton>
        <Title>장바구니</Title>
      </Header>

      <CartContainer>
        <CartTable>
          <CartHeader>
            <div>상품정보</div>
            <div>수량</div>
            <div>가격</div>
            <div>총 금액</div>
            <div></div>
          </CartHeader>

          {cartItems.map((item) => (
            <CartItem key={item.id}>
              <ProductInfo>
                <ProductImage src={item.image} alt={item.title} />
                <ProductDetails>
                  <ProductTitle>{item.title}</ProductTitle>
                  <ProductCategory>{item.category}</ProductCategory>
                </ProductDetails>
              </ProductInfo>

              <QuantityControl>
                <QuantityButton onClick={() => updateQuantity(item.id, -1)}>
                  <Minus size={16} />
                </QuantityButton>
                <Quantity>{item.quantity}</Quantity>
                <QuantityButton onClick={() => updateQuantity(item.id, 1)}>
                  <Plus size={16} />
                </QuantityButton>
              </QuantityControl>

              <Price>{item.price.toLocaleString()}원</Price>
              <Total>{(item.price * item.quantity).toLocaleString()}원</Total>

              <DeleteButton onClick={() => removeItem(item.id)}>
                <Trash2 size={16} />
              </DeleteButton>
            </CartItem>
          ))}
        </CartTable>

        <Summary>
          <SummaryContent>
            <SummaryRow>
              <span>상품 금액</span>
              <span>{subtotal.toLocaleString()}원</span>
            </SummaryRow>
            <SummaryRow>
              <span>배송비</span>
              <span>{shipping.toLocaleString()}원</span>
            </SummaryRow>
            <SummaryRow>
              <span>결제 금액</span>
              <span>{total.toLocaleString()}원</span>
            </SummaryRow>
            <CheckoutButton onClick={() => navigate('/Checkout')}>서비스별 의뢰 · 결제</CheckoutButton>
          </SummaryContent>
        </Summary>
      </CartContainer>
    </Container>
  )
}
